import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import DashboardLayout from "./components/Layout";
import useRole from "./components/useRole";
import { getRefunds, retryRefund } from "../../api/subscription";

const statusBadge = {
  success: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-700",
};

// Money that was only blocked is let go; money already taken has to travel back
const statusLabel = (row) => {
  const done = row?.wasHeld ? "Buraxıldı" : "Qaytarıldı";
  return { success: done, pending: "Gözləyir", failed: "Alınmadı" }[row?.refund?.status] || row?.refund?.status || "—";
};

const reasonLabel = {
  "seat-taken": "Yer başqasına satılmışdı",
  "capture-failed": "Ödəniş tamamlana bilmədi",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date)) return "—";
  return date.toLocaleString("az-AZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const seatsLabel = (seats) =>
  (seats || [])
    .map((seat) => seat?.label || [seat?.zone, seat?.number].filter(Boolean).join(" "))
    .filter(Boolean)
    .join(", ") || "—";

// Payments that were taken and then given back. So far this only happens when
// two buyers paid for the same seat: the later order is cancelled and reversed
// through epoint, and whatever epoint answered is kept here.
const RefundsPage = () => {
  const { eventId } = useParams();
  const { isAdmin } = useRole();
  const [refunds, setRefunds] = useState(null);
  const [status, setStatus] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const load = useCallback(async () => {
    const response = await getRefunds(eventId);
    setRefunds(response?.data?.items || []);
  }, [eventId]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const totals = useMemo(() => {
    const rows = refunds || [];
    const sum = (list) => list.reduce((total, row) => total + (row.amount || 0), 0);
    const of = (state) => rows.filter((row) => row.refund?.status === state);
    return {
      count: rows.length,
      amount: sum(rows),
      open: of("pending").length + of("failed").length,
      openAmount: sum([...of("pending"), ...of("failed")]),
    };
  }, [refunds]);

  if (!isAdmin) return <Navigate to={`/dashboard/${eventId}`} replace />;

  const onRetry = async (row) => {
    setRetryingId(row._id);
    setStatus(null);
    try {
      const response = await retryRefund(eventId, row._id);
      setStatus(
        response?.data?.success
          ? { type: "success", message: "Məbləğ geri qaytarıldı." }
          : { type: "error", message: response?.data?.message || "Geri qaytarma alınmadı." }
      );
      await load();
    } catch (error) {
      setStatus({ type: "error", message: error?.response?.data?.message || "Geri qaytarma alınmadı." });
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <DashboardLayout id={eventId}>
      <div className="mt-6 lg:mt-10 space-y-6">
        <div>
          <h2 className="text-2xl font-bold">💸 Ləğv edilən ödənişlər</h2>
          <p className="text-sm text-gray-500 mt-1">
            Bilet yazılmayan ödənişlər — adətən seçilmiş yer bir neçə saniyə əvvəl başqasına satıldığına görə.
            Bloklanmış məbləğ dərhal buraxılır, alınmış məbləğ isə epoint üzərindən geri qaytarılır;
            alınmayan hallarda yenidən cəhd edə bilərsiniz.
          </p>
        </div>

        {status && (
          <div
            className={`rounded-xl px-4 py-3 text-sm ${
              status.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
            }`}
          >
            {status.message}
          </div>
        )}

        {refunds === null ? (
          <div className="bg-white rounded-xl shadow p-6 animate-pulse space-y-3">
            <div className="h-4 bg-surface-100 rounded w-1/3" />
            <div className="h-4 bg-surface-100 rounded w-2/3" />
            <div className="h-4 bg-surface-100 rounded w-1/2" />
          </div>
        ) : refunds.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-10 text-center">
            <div className="text-4xl mb-3">✅</div>
            <h3 className="font-semibold text-surface-900">Ləğv edilən ödəniş yoxdur</h3>
            <p className="text-sm text-gray-500 mt-1">
              Bu tədbirdə hər ödənişin biləti yazılıb.
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="bg-white rounded-xl shadow p-5">
                <div className="text-xs uppercase tracking-wide text-gray-500">Ümumi</div>
                <div className="mt-1 text-2xl font-bold text-surface-900">
                  {totals.count} ödəniş · {totals.amount.toFixed(2)} AZN
                </div>
              </div>
              <div className="bg-white rounded-xl shadow p-5">
                <div className="text-xs uppercase tracking-wide text-gray-500">Gözləyən / alınmayan</div>
                <div className={`mt-1 text-2xl font-bold ${totals.open ? "text-red-600" : "text-surface-900"}`}>
                  {totals.open} ödəniş · {totals.openAmount.toFixed(2)} AZN
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Alıcı</th>
                    <th className="px-4 py-3">Yer</th>
                    <th className="px-4 py-3">Məbləğ</th>
                    <th className="px-4 py-3">Ödəniş</th>
                    <th className="px-4 py-3">Geri qaytarma</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {refunds.map((row) => (
                    <tr key={row._id} className="align-top">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-surface-900">{row.name}</div>
                        <div className="text-xs text-gray-500">{row.email}</div>
                        <div className="text-xs text-gray-500">{row.phone}</div>
                        <div className="text-xs text-gray-400 mt-1">
                          Sifariş: {String(row._id).slice(-8).toUpperCase()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-surface-900">{seatsLabel(row.refund?.seats || row.seats)}</div>
                        {row.ticketTypeName && (
                          <div className="text-xs text-gray-500">{row.ticketTypeName}</div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {reasonLabel[row.refund?.reason] || row.refund?.reason}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-semibold text-surface-900">
                        {(row.amount || 0).toFixed(2)} AZN
                        {row.voucherCode && (
                          <div className="text-xs font-normal text-gray-500">Voucher: {row.voucherCode}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        <div>{formatDateTime(row.paymentDate)}</div>
                        {row.paymentTransaction && (
                          <div className="text-gray-400 mt-1">{row.paymentTransaction}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                            statusBadge[row.refund?.status] || "bg-surface-100 text-surface-600"
                          }`}
                        >
                          {statusLabel(row)}
                        </span>
                        {row.wasHeld && (
                          <div className="text-xs text-gray-400 mt-1">Məbləğ alınmamışdı, yalnız bloklanmışdı</div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDateTime(row.refund?.completedAt || row.refund?.requestedAt)}
                        </div>
                        {row.refund?.note && (
                          <div className="text-xs text-gray-500 mt-1 max-w-[18rem]">{row.refund.note}</div>
                        )}
                        {row.refund?.message && (
                          <div className="text-xs text-red-600 mt-1 max-w-[18rem]">{row.refund.message}</div>
                        )}
                        {row.refund?.traceId && (
                          <div className="text-xs text-gray-400 mt-1">trace: {row.refund.traceId}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {row.refund?.status !== "success" && (
                          <button
                            type="button"
                            onClick={() => onRetry(row)}
                            disabled={retryingId === row._id}
                            className="inline-flex items-center rounded-xl bg-surface-900 px-3 py-2 text-xs font-semibold text-white hover:bg-surface-800 disabled:opacity-50 transition"
                          >
                            {retryingId === row._id ? "Göndərilir..." : "Yenidən cəhd et"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default RefundsPage;
